import { useEffect, type ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { MangoProvider } from '@humanforce/mango-ui-react';
import { authService } from './auth/service';
import { LoginPage } from './components/auth/LoginPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { UnauthenticatedLayout } from './components/auth/UnauthenticatedLayout';
import { MainLayout } from './components/layout/MainLayout';
import { itemPath, LEGACY_REDIRECTS, navSections } from './navigation/navConfig';
import { pageRegistry } from './pages';
import { HomePage } from './pages/HomePage';
import { PayRunDetailPage } from './pages/PayRunDetailPage';
import { OrganisationPage } from './pages/OrganisationPage';

interface RouteConfig {
  key: string;
  path: string;
  element: ReactElement;
  requiredRole?: string;
}

const ContextPreservingRedirect = ({ to }: { to: string }) => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
};

// Build the route table from the data-driven navigation config so adding or
// reshaping navigation never requires hand-editing routes here.
const buildItemRoutes = (): RouteConfig[] => {
  const routes: RouteConfig[] = [];
  for (const section of navSections)
    for (const item of section.items ?? []) {
      const Page = pageRegistry[itemPath(section, item)];
      if (!Page) continue;
      const rel = `${section.slug}/${item.slug}`;
      if (item.subItems?.length) {
        // Item route redirects to the first tab; each sub-item tab is URL-addressable.
        routes.push({ key: rel, path: rel, element: <ContextPreservingRedirect to={item.subItems[0].slug} />, requiredRole: item.requiredRole });
        routes.push({ key: `${rel}/*`, path: `${rel}/*`, element: <Page />, requiredRole: item.requiredRole });
      } else {
        routes.push({ key: rel, path: rel, element: <Page />, requiredRole: item.requiredRole });
      }
    }
  return routes;
};

const protectedElement = (element: ReactElement, requiredRole?: string) => (
  <ProtectedRoute requiredRole={requiredRole}>
    <MainLayout>{element}</MainLayout>
  </ProtectedRoute>
);

const LegacyRedirect = ContextPreservingRedirect;

// Tenant compatibility entries are intentionally exact-only: every retired URL is enumerated
// in LEGACY_REDIRECTS. Admin additionally supports subtree redirects because its historical
// Organisation resource paths carry arbitrary route tails; Tenant resource routes remain live.

export const App = () => {
  useEffect(() => {
    void authService.initFromStorage();
  }, []);

  return (
    <MangoProvider>
      <Routes>
        <Route path="/login" element={<UnauthenticatedLayout><LoginPage /></UnauthenticatedLayout>} />
        <Route path="/" element={protectedElement(<HomePage />)} />
        {buildItemRoutes().map(({ key, path, element, requiredRole }) => (
          <Route key={key} path={path} element={protectedElement(element, requiredRole)} />
        ))}
        {LEGACY_REDIRECTS.map(({ from, to }) => (
          <Route key={from} path={from.slice(1)} element={protectedElement(<LegacyRedirect to={to} />)} />
        ))}
        <Route path="maintenance/stp-setup/declarers" element={protectedElement(<LegacyRedirect to="/" />)} />
        <Route path="maintenance/stp-setup/payer-details" element={protectedElement(<LegacyRedirect to="/maintenance/stp-setup/payer-readiness" />)} />
        <Route path="maintenance/stp-setup/ato-connection" element={protectedElement(<LegacyRedirect to="/maintenance/stp-setup/payer-readiness" />)} />
        <Route path="administration/system-parameters" element={protectedElement(<LegacyRedirect to="/maintenance/superannuation/system-parameters" />)} />
        <Route
          path="payroll/pay-runs/:payId"
          element={protectedElement(<PayRunDetailPage />)}
        />
        <Route path="maintenance/organisation/*" element={protectedElement(<OrganisationPage />)} />
        <Route path="maintenance/organisations/:organisationId/:tab" element={protectedElement(<OrganisationPage />)} />
        <Route path="maintenance/organisations/:organisationId/business-records/:businessRecordId/:tab" element={protectedElement(<OrganisationPage />)} />
        <Route path="*" element={protectedElement(<Navigate to="/" replace />)} />
      </Routes>
    </MangoProvider>
  );
};
